import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import axiosUserInstance from './axiosUserInstance';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const [cartCount, setCartCount] = useState(0);
    const [role, setRole] = useState(null);
    const [coordinate, setCoordinate] = useState({
        lat: 0,
        lng: 0,
    });
    const [currentLoginUserId, setCurrentLoginUserId] = useState(null);
    const [visitedUserId, setVisitedUserId] = useState(null);
    const [profileImage, setProfileImage] = useState(null);
    const [userData, setUserData] = useState(null);
    const [isLocation, setIsLocation] = useState(null);
    const [followStatusMap, setFollowStatusMap] = useState({});
    const [offerModelPercentage, setOfferModelPercentage] = useState(null);
    const [deliveryCharge, setDeliveryCharge] = useState();
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [vendorProfileImage, setVendorProfileImage] = useState(null);
    const [vendorUserData, setVendorUserData] = useState(null);
    const [vendorAddress, _setVendorAddress] = useState(null);
    const [currentVendorId, setCurrentVendorId] = useState(null);
    const token = localStorage.getItem('authToken');

    const setVendorAddress = (address) => {
        _setVendorAddress(address);
        localStorage.setItem('vendorAddress', JSON.stringify(address));
    };
        
    useEffect(() => {
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                setCurrentLoginUserId(decodedToken?.id);
            } catch (error) {
                console.error('Invalid token:', error);
                setCurrentLoginUserId(null);
            }
        } else {
            setCurrentLoginUserId(null);
        }
    }, [token]);

    const fetchUserProfile = async () => {
        try {
            const response = await axiosUserInstance.get('/customers/account');
            const data = response?.data?.profileDetails[0];
            setUserData(data);
            setProfileImage(data?.profile_picture);
        } catch (error) {
            console.log('Error fetching user data:', error);
        }
    };

    const fetchVendorUserProfile = async () => {
        try {
            const response = await axiosUserInstance.get('/auth/user/userbyId');
            const data = response?.data?.user;
            setVendorUserData(data);
            setVendorProfileImage(data?.profile_picture);
        } catch (error) {
            console.log('Error fetching user data:', error);
        }
    };

    useEffect(() => {
        if (token) {
            fetchUserProfile();
            fetchVendorUserProfile();
        }
    }, [token]);

    useEffect(() => {
        const token = localStorage.getItem('authToken');

        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                setCurrentLoginUserId(decodedToken?.id);
            } catch (error) {
                console.error('Invalid token:', error);
                setCurrentLoginUserId(null);
            }
        }
    }, [currentLoginUserId]);

    const location = () => {
        const stored = localStorage.getItem('selectedLocation');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed?.lat && parsed?.lng) {
                    setCoordinate({ lat: parsed.lat, lng: parsed.lng });
                }
            } catch (error) {
                console.error('Invalid selectedLocation in localStorage:', error);
            }
        }
    };

    useEffect(() => {
    // Initial load
        location();

        // Sync across tabs or location changes
        const handleStorageChange = (e) => {
            if (e.key === 'selectedLocation') {
                location();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);


    const fetchCartCount = async () => {
        if (!token) {
            setCartCount(0);
            return;
        }
        const location = JSON.parse(localStorage.getItem('selectedLocation'));

        const lat = location?.lat;
        const lng = location?.lng;

        try {
            const response = await axiosUserInstance.get('/cartdetails/cartsp', {
                params: {
                    latitude: lat,
                    longitude: lng,
                }
            });
            setCartCount(response?.data?.cartDetails[0]?.Cart_Information?.length);
        } catch (error) {
            console.log('Failed to fetch cart count:', error);
        }
    };

    const toggleFollow = (userId, status) => {
        setFollowStatusMap((prev) => ({
            ...prev,
            [userId]: status,
        }));
    };

    useEffect(() => {
        fetchCartCount();
        location();
    }, [token, cartCount]);

    return (
        <CartContext.Provider
            value={{
                cartCount,
                setCartCount,
                fetchCartCount,
                role,
                setRole,
                coordinate,
                currentLoginUserId,
                setCurrentLoginUserId,
                visitedUserId,
                setVisitedUserId,
                profileImage,
                userData,
                setUserData,
                fetchUserProfile,
                isLocation,
                setIsLocation,
                followStatusMap,
                toggleFollow,
                offerModelPercentage,
                setOfferModelPercentage,
                deliveryCharge,
                setDeliveryCharge,
                setSelectedAddress,
                selectedAddress,
                vendorProfileImage,
                vendorUserData,
                setVendorUserData,
                fetchVendorUserProfile,
                vendorAddress,
                setVendorAddress,
                currentVendorId,
                setCurrentVendorId,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    return useContext(CartContext);
};
